import { Check, X } from "lucide-react";
import { motion } from "framer-motion";

/**
 * DiscoveryTracker — compact 6-item checklist of discovery information.
 * Sections are auto-marked complete when the AI has captured >=1 item.
 * Visible only during the Discovery phase (parent controls visibility).
 */
const ITEMS = [
  { key: "pain_points", label: "Pain / Problems" },
  { key: "goals", label: "Goals" },
  { key: "previous_attempts", label: "Previous Attempts" },
  { key: "cost_of_inaction", label: "Cost of Inaction" },
  { key: "decision_maker", label: "Decision Maker" },
  { key: "financial_situation", label: "Financial Situation" },
];

export default function DiscoveryTracker({ state }) {
  const completed = ITEMS.filter((it) => (state[it.key] || []).length > 0).length;
  const total = ITEMS.length;
  const pct = Math.round((completed / total) * 100);

  return (
    <motion.section
      data-testid="discovery-tracker"
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="font-display text-xs font-semibold uppercase tracking-widest text-slate-500">
            Discovery Checklist
          </h3>
          <span
            data-testid="discovery-progress"
            className="text-[10px] font-mono text-slate-500"
          >
            {completed}/{total}
          </span>
        </div>
        <div className="h-1.5 w-24 bg-slate-100 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-emerald-500"
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>

      <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
        {ITEMS.map((it) => {
          const count = (state[it.key] || []).length;
          const done = count > 0;
          return (
            <li
              key={it.key}
              data-testid={`discovery-item-${it.key}`}
              data-complete={done ? "true" : "false"}
              className="flex items-center justify-between text-sm"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${
                    done
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-orange-100 text-orange-700"
                  }`}
                >
                  {done ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                </span>
                <span className={done ? "text-slate-800" : "text-slate-500"}>{it.label}</span>
              </div>
              <span className="font-mono text-xs text-slate-400 shrink-0">{count}</span>
            </li>
          );
        })}
      </ul>
    </motion.section>
  );
}
